import { Can } from "@casl/react";
import { PencilIcon, ShareIcon } from "@heroicons/react/24/outline";
import { ActionButton } from "@via/components/action-button/action-button";
import {
  SkeletonLoader,
  Spinner,
  TitleLoader,
} from "@via/components/loading/loading";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { useAbility } from "@via/context/ability-context";
import { useAuth } from "@via/hooks/useAuth";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import AddPipelineModal from "@via/pages/projects/add-pipeline-modal";
import DeleteProjectModal from "@via/pages/projects/delete-project-modal.tsx";
import PersistentRoutes from "@via/pages/projects/PersistentRoutes.tsx";
import {
  useProject,
  useUpdateProject,
} from "@via/pages/projects/projects.api.tsx";
import ShareProjectModal from "@via/pages/projects/share-project-modal.tsx";
import {
  BUTTONS,
  KEYS,
  PROJECT_DETAIL_CONTEXT_MENU_OPTIONS,
  PROJECTS_TABS,
  ROUTES,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { classNames, formatDateString } from "@via/utils/helpers";
import type { TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { UserRole, type ListProjectsResponseBodyItem } from "common";
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export default function Project(): JSX.Element {
  const { config, user } = useAuth();
  const isAdmin = user.userRole === USER_ROLES.ADMIN;

  const measureRef = useRef<HTMLSpanElement>(null);
  const projectNameInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { projectId = "", pipelineId = "" } = useParams();
  const {
    data: projectData,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useProject(projectId);
  const { mutate: updateProject, isPending: isUpdateProjectLoading } =
    useUpdateProject();

  const [projectNameInputWidth, setProjectNameInputWidth] =
    useState<number>(100);
  const [projectName, setProjectName] = useState("");
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [isAddPipelineModalOpen, setIsAddPipelineModalOpen] = useState(false);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [isShareProjectModalOpen, setIsShareProjectModalOpen] = useState(false);

  const { setSelectedProject } = useGlobal();
  const { setNotification, NOTIFICATION } = useNotifications();
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: projectData?.own,
    },
  ]);
  const [projectToBeDeleted, setProjectToBeDeleted] =
    useState<ListProjectsResponseBodyItem | null>(null);

  const handleProjectContextMenuAction = (
    action: TableContextMenuInterface
  ) => {
    switch (action.name) {
      case "Delete Project":
        projectData && setProjectToBeDeleted(projectData);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (projectData) {
      setSelectedProject(projectData);
      setProjectName(projectData.name);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      navigate(ROUTES.PROJECTS);
      setNotification({
        id: "fetch-project-failed",
        message: "Project Fetch Failed",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [isError, error]);

  const horizontalTabOptions = useMemo(() => {
    setIsTabLoading(true);
    if (projectData) {
      return PROJECTS_TABS.filter(tab => {
        if (
          tab.name === "Metadata" &&
          !(projectData.showVmeta && config?.ENABLE_VMETA)
        )
          return false;
        if (
          tab.name === "Portal" &&
          !(
            projectData.showVportal &&
            config?.ENABLE_VPORTAL &&
            user.userRole !== UserRole.Guest
          )
        )
          return false;
        setIsTabLoading(false);
        return true;
      });
    }
    return PROJECTS_TABS;
  }, [projectData]);

  const handleCreatePipelineClick = () => {
    navigate({ pathname: ROUTES.PIPELINES, search: "?new=true" });
  };

  const handleProjectNameUpdate = useCallback((value: string) => {
    setProjectName(value);
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (measureRef?.current) {
        const measuredWidth = measureRef.current?.offsetWidth;
        const newWidth = Math.max(100, Math.min(measuredWidth + 30, 384)); // min 100px, max-w-96
        setProjectNameInputWidth(newWidth);
      }
    };

    updateWidth();
  }, [projectName, measureRef?.current]);

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        {isLoading ? (
          <TitleLoader />
        ) : (
          <div className="flex items-center">
            <span
              ref={measureRef}
              className="invisible fixed -left-[9999px] text-2xl font-bold md:text-3xl"
              aria-hidden="true">
              {projectName}
            </span>
            <input
              ref={projectNameInputRef}
              type="text"
              className={classNames(
                "mr-1 max-w-96 border-none text-2xl font-bold leading-10 text-gray-900 sm:truncate sm:tracking-tight md:text-3xl md:leading-normal",
                ability.can("View", "Button")
                  ? "hover:bg-gray-100 ease-in-out duration-300"
                  : ""
              )}
              style={{
                width: `${projectNameInputWidth}px`,
                transition: "width 0.2s ease",
              }}
              disabled={!ability.can("View", "Button")}
              placeholder="Enter Project Name"
              onClick={() => setIsEditingProjectName(true)}
              onChange={e => handleProjectNameUpdate(e.target.value)}
              onBlur={() => {
                if (projectName !== projectData?.name) {
                  updateProject({
                    id: projectId,
                    data: { name: projectName },
                  });
                }
                setIsEditingProjectName(false);
              }}
              value={projectName}
            />
            {ability.can("View", "Button") && !isEditingProjectName && (
              <button
                data-testid="edit-project-name"
                onClick={() => {
                  projectNameInputRef.current?.focus();
                  setIsEditingProjectName(true);
                }}
                className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-600"
                disabled={isUpdateProjectLoading}
                title="Edit project name">
                <PencilIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className="flex gap-2">
          {pathname.includes(KEYS.PIPELINES) && (
            <Can I="View" a="Button" ability={ability}>
              <div className="flex gap-2">
                <ActionButton
                  button={BUTTONS.CREATE_PIPELINE}
                  primary={true}
                  onClick={handleCreatePipelineClick}
                />
                <ActionButton
                  button={BUTTONS.ADD_PIPELINE}
                  primary={true}
                  onClick={() => setIsAddPipelineModalOpen(true)}
                />
              </div>
            </Can>
          )}
          {config?.ENABLE_SHARE_BUTTON && (
            <button
              type="button"
              disabled={isLoading || !ability.can("View", "Button")}
              onClick={() => setIsShareProjectModalOpen(true)}
              className="bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md inline-flex gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              {isLoading ? (
                <Spinner loading={isLoading} />
              ) : (
                <ShareIcon
                  className="-ml-0.5 mr-0.5 h-5 w-5"
                  aria-hidden="true"
                />
              )}
              Share
            </button>
          )}
          {!isLoading && (isAdmin || projectData?.own) ? (
            <TableContextMenu
              options={PROJECT_DETAIL_CONTEXT_MENU_OPTIONS}
              onSelect={handleProjectContextMenuAction}
              className="rounded-md border border-indigo-600 px-2 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:border-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            />
          ) : null}
        </div>
      </div>
      {!!projectToBeDeleted && (
        <DeleteProjectModal
          project={projectToBeDeleted}
          setProject={() => {
            setProjectToBeDeleted(null);
          }}
        />
      )}
      {!isLoading && (
        <span className="text-xs text-gray-500">
          Created by {projectData?.owner} on{" "}
          {formatDateString(projectData?.createdAt ?? "", "LLL")} • Last edited
          on {""}
          {formatDateString(projectData?.updatedAt ?? "", "LLL")}
        </span>
      )}
      {isAddPipelineModalOpen && (
        <AddPipelineModal
          open={isAddPipelineModalOpen}
          setOpen={setIsAddPipelineModalOpen}
          projectId={projectId}
          pipelineId={pipelineId}
        />
      )}
      {isShareProjectModalOpen && (
        <ShareProjectModal
          open={isShareProjectModalOpen}
          setOpen={setIsShareProjectModalOpen}
          projectId={projectId}
          groupId={projectData?.groupId}
          sharedGroupId={projectData?.sharedGroupId}
          permission={projectData?.permission}
        />
      )}
      {isTabLoading ? (
        <div className="grid grid-cols-2 gap-6 py-6 sm:grid-cols-3 lg:grid-cols-4">
          <SkeletonLoader count={12} />
        </div>
      ) : (
        <>
          <HorizontalTabs
            buttonIdPrefix="project"
            options={horizontalTabOptions}
          />
          <PersistentRoutes horizontalTabOptions={horizontalTabOptions} />
        </>
      )}
    </main>
  );
}
